// Team + Departments views of the Workload tab. Team: one row per person in
// the caller's workload scope (server-resolved) with the six counts, the band
// chip + "Why?", and a thin bar relative to the busiest visible row (no
// percentage text). Departments: totals per department; clicking drills to
// Team filtered by that department. Rows never show item titles here —
// `item_visibility` only decides what the drill-down may list.
// Phase 2 adds a Capacity column: a percentage ONLY for people whose capacity
// is configured (else "Capacity not set"), hidden entirely when the policy's
// `workload_show_utilization` is false; sort "Headroom" is server-side.
// Phase 3 adds, under the tiles: the Suggested-moves panel (proposals only;
// acting = the existing reassign dialog) and the population's 30-day trends.
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { ChevronRight, Loader2, Search } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Input } from '@/features/shared/components/ui/input';
import { Button } from '@/features/shared/components/ui/button';
import { useDebouncedValue } from '@/features/shared/components/ListWorkbench';
import { getApiErrorMessage } from '@/lib/apiError';
import { useWorkPolicy } from '@/features/work/api/hooks';
import { useWorkloadAttention, useWorkloadDepartments, useWorkloadPeople, useWorkloadSummary } from '@/features/work/api/workloadHooks';
import type { StaleBasis, WorkloadDepartmentRow, WorkloadPersonRow, WorkloadSort } from '@/services/workloadService';
import type { WorkHealth } from '@/services/workService';
import { ATTENTION_FILTERS, NeedsAttention } from './NeedsAttention';
import { CapacityCell } from './capacity';
import { BAND_BAR_CLASS, BandChip, healthLabel } from './health';
import { PersonDrilldown } from './PersonDrilldown';
import { SuggestionsPanel } from './Suggestions';
import { SummaryTiles } from './tiles';
import { TrendStrip } from './trends';

const PAGE = 25;
const sel = 'rounded-md border border-border bg-background text-foreground text-sm px-2.5 py-2';

export interface WorkloadPeopleProps {
  departmentId?: string;
  onDepartment: (id: string) => void;
  staleAfterDays?: number;
  staleBasis?: StaleBasis;
  dueSoonDays?: number;
  refreshKey?: number;
}

/** Thin relative bar: width = score / max score among visible rows. */
export const relativeWidth = (score: number, max: number): number =>
  max > 0 ? Math.max(4, Math.min(100, Math.round((score / max) * 100))) : 0;

export const WorkloadTeam: React.FC<WorkloadPeopleProps> = ({ departmentId, onDepartment, staleAfterDays, staleBasis, dueSoonDays, refreshKey: outerKey = 0 }) => {
  const { t } = useTranslation('work');
  // A reassignment made from the suggestions panel refetches everything here.
  const [bump, setBump] = useState(0);
  const refreshKey = outerKey + bump;
  const tt = t as unknown as (key: string, opts?: Record<string, unknown>) => string;
  const [q, setQ] = useState('');
  const qDebounced = useDebouncedValue(q, 300);
  const [health, setHealth] = useState<WorkHealth | ''>('');
  const [sort, setSort] = useState<WorkloadSort>('workload');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<WorkloadPersonRow[]>([]);
  const [open, setOpen] = useState<WorkloadPersonRow | null>(null);
  const [attentionHealth, setAttentionHealth] = useState<WorkHealth | ''>('');
  const { policy } = useWorkPolicy();
  // Utilization (the Capacity column, the Headroom sort, the summary line) is a
  // tenant choice; an older backend without the flag shows it.
  const showUtilization = policy?.workload_show_utilization !== false;
  useEffect(() => { if (!showUtilization && sort === 'headroom') setSort('workload'); }, [showUtilization, sort]);

  const params = useMemo(() => ({
    q: qDebounced || undefined, department_id: departmentId || undefined, health: health || undefined, sort, page, page_size: PAGE,
  }), [qDebounced, departmentId, health, sort, page]);
  useEffect(() => { setPage(1); }, [qDebounced, departmentId, health, sort]);

  const people = useWorkloadPeople(params, true, refreshKey);
  const summary = useWorkloadSummary({ department_id: departmentId || undefined }, true, refreshKey);
  const departments = useWorkloadDepartments(true, refreshKey);
  const attention = useWorkloadAttention(
    { health: attentionHealth || undefined, department_id: departmentId || undefined, page_size: PAGE }, true, refreshKey,
  );

  useEffect(() => {
    if (!people.data) return;
    setRows((prev) => (people.data!.page > 1 ? [...prev, ...people.data!.items] : people.data!.items));
  }, [people.data]);
  useEffect(() => {
    if (people.error) toast.error(getApiErrorMessage(people.error, t('people_load_failed', { defaultValue: 'Could not load team workload.' })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [people.error]);

  const maxScore = useMemo(() => rows.reduce((m, r) => Math.max(m, r.workload.score || 0), 0), [rows]);
  const deptName = departmentId ? departments.data?.find((d) => d.department_id === departmentId)?.department_name : undefined;
  const capacitySummary = summary.data?.capacity;

  return (
    <div className="space-y-4">
      <SummaryTiles totals={summary.data?.totals} loading={summary.loading && !summary.data} staleAfterDays={staleAfterDays} staleBasis={staleBasis} dueSoonDays={dueSoonDays} />
      {showUtilization && capacitySummary && capacitySummary.configured_people > 0 && (
        <p className="text-xs text-muted-foreground" data-testid="capacity-summary-line">
          {capacitySummary.configured_people === 1
            ? t('capacity_summary_line_single', {
                defaultValue: 'Capacity configured for 1 person · {{over}} over capacity',
                over: capacitySummary.over_capacity,
              })
            : t('capacity_summary_line', {
                defaultValue: 'Capacity configured for {{count}} people · {{over}} over capacity',
                count: capacitySummary.configured_people, over: capacitySummary.over_capacity,
              })}
        </p>
      )}
      {people.data?.scope_summary && (
        <p className="text-xs text-muted-foreground" data-testid="workload-scope">
          {t('scope_line', { defaultValue: 'Showing {{scope}}', scope: people.data.scope_summary })}
          {summary.data ? ` · ${t('people_count', { defaultValue: '{{count}} people', count: summary.data.population })}` : ''}
        </p>
      )}

      <SuggestionsPanel departmentId={departmentId} refreshKey={outerKey} onChanged={() => setBump((b) => b + 1)} />
      <TrendStrip departmentId={departmentId} showCapacity={showUtilization} refreshKey={refreshKey} testId="team-trends" />

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-8"
            placeholder={t('people_search_ph', { defaultValue: 'Search people…' }) as string}
            aria-label={t('people_search', { defaultValue: 'Search people' }) as string} />
        </div>
        <select className={sel} value={departmentId || ''} onChange={(e) => onDepartment(e.target.value)}
          aria-label={t('filter_department', { defaultValue: 'Department' }) as string}>
          <option value="">{t('all_departments', { defaultValue: 'All departments' })}</option>
          {(departments.data || []).map((d) => <option key={d.department_id} value={d.department_id}>{d.department_name}</option>)}
        </select>
        <select className={sel} value={health} onChange={(e) => setHealth(e.target.value as WorkHealth | '')}
          aria-label={t('filter_health', { defaultValue: 'Health' }) as string}>
          <option value="">{t('any_health', { defaultValue: 'Any health' })}</option>
          {ATTENTION_FILTERS.map((h) => <option key={h} value={h}>{healthLabel(tt, h)}</option>)}
        </select>
        <select className={sel} value={sort} onChange={(e) => setSort(e.target.value as WorkloadSort)}
          aria-label={t('sort', { defaultValue: 'Sort' }) as string}>
          <option value="workload">{t('sort_workload', { defaultValue: 'Workload' })}</option>
          <option value="overdue">{t('sort_overdue', { defaultValue: 'Overdue' })}</option>
          <option value="due_soon">{t('sort_due_soon', { defaultValue: 'Due soon' })}</option>
          <option value="name">{t('sort_name', { defaultValue: 'Name' })}</option>
          {showUtilization && <option value="headroom">{t('sort_headroom', { defaultValue: 'Headroom' })}</option>}
        </select>
      </div>

      {deptName && (
        <p className="text-sm text-muted-foreground">
          {t('dept_filter_line', { defaultValue: 'Department: {{name}}', name: deptName })}
          <button type="button" className="ml-2 text-primary-500 hover:underline" onClick={() => onDepartment('')}>
            {t('clear', { defaultValue: 'Clear' })}
          </button>
        </p>
      )}

      <Card><CardContent className="p-0 overflow-x-auto">
        {people.loading && rows.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground flex items-center gap-2" role="status">
            <Loader2 className="w-4 h-4 animate-spin" /> {t('common.loading', { defaultValue: 'Loading…' })}
          </p>
        ) : people.error ? (
          <p className="p-6 text-sm text-muted-foreground" role="alert">
            {getApiErrorMessage(people.error, t('people_load_failed', { defaultValue: 'Could not load team workload.' }))}
          </p>
        ) : rows.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground" data-testid="people-empty">
            {q || health ? t('people_no_match', { defaultValue: 'No people match your filters.' }) : t('people_none', { defaultValue: 'No one in your workload scope yet.' })}
          </p>
        ) : (
          <table className="w-full text-sm" data-testid="people-table">
            <thead>
              <tr className="text-xs text-muted-foreground">
                <th scope="col" className="text-left font-medium py-2 px-4">{t('col_person', { defaultValue: 'Person' })}</th>
                <th scope="col" className="font-medium py-2 px-2 text-right">{t('tile_active', { defaultValue: 'Active' })}</th>
                <th scope="col" className="font-medium py-2 px-2 text-right">{t('tile_due_soon', { defaultValue: 'Due soon' })}</th>
                <th scope="col" className="font-medium py-2 px-2 text-right">{t('tile_overdue', { defaultValue: 'Overdue' })}</th>
                <th scope="col" className="font-medium py-2 px-2 text-right">{t('tile_stale', { defaultValue: 'Stale' })}</th>
                <th scope="col" className="font-medium py-2 px-2 text-right">{t('tile_awaiting_review', { defaultValue: 'Awaiting review' })}</th>
                <th scope="col" className="font-medium py-2 px-2 text-right">{t('col_on_hold', { defaultValue: 'On hold' })}</th>
                <th scope="col" className="text-left font-medium py-2 px-4 min-w-[220px]">{t('col_workload', { defaultValue: 'Workload' })}</th>
                {showUtilization && <th scope="col" className="text-left font-medium py-2 px-4">{t('col_capacity', { defaultValue: 'Capacity' })}</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r) => {
                const subline = [r.person.job_title, r.person.department_name].filter(Boolean).join(' · ');
                const w = relativeWidth(r.workload.score || 0, maxScore);
                return (
                  <tr key={r.person.employee_id} data-testid={`person-row-${r.person.employee_id}`} className="hover:bg-muted/40">
                    <td className="py-2.5 px-4 align-middle">
                      <button type="button" className="text-left min-w-0" onClick={() => setOpen(r)}>
                        <span className="block text-foreground truncate hover:underline">{r.person.display_name}</span>
                        {subline && <span className="block text-[11px] text-muted-foreground truncate">{subline}</span>}
                      </button>
                    </td>
                    <td className="py-2.5 px-2 text-right tabular-nums">{r.summary.active}</td>
                    <td className="py-2.5 px-2 text-right tabular-nums">{r.summary.due_soon}</td>
                    <td className="py-2.5 px-2 text-right tabular-nums">{r.summary.overdue}</td>
                    <td className="py-2.5 px-2 text-right tabular-nums">{r.summary.stale}</td>
                    <td className="py-2.5 px-2 text-right tabular-nums">{r.summary.awaiting_review}</td>
                    <td className="py-2.5 px-2 text-right tabular-nums">{r.summary.paused}</td>
                    <td className="py-2.5 px-4 align-middle">
                      <BandChip band={r.workload.band} reasons={r.workload.reasons} confidence={r.workload.confidence} />
                      <div className="mt-1.5 h-1 w-full rounded-full bg-muted" aria-hidden="true">
                        <div className={`h-1 rounded-full ${BAND_BAR_CLASS[r.workload.band] || 'bg-muted-foreground/40'}`}
                          style={{ width: `${w}%` }} data-testid={`band-bar-${r.person.employee_id}`} />
                      </div>
                    </td>
                    {showUtilization && (
                      <td className="py-2.5 px-4 align-middle">
                        <CapacityCell capacity={r.capacity} testId={`capacity-${r.person.employee_id}`} />
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </CardContent></Card>
      {people.data?.has_more && (
        <div className="flex justify-center">
          <Button variant="outline" size="sm" disabled={people.loading} onClick={() => setPage((p) => p + 1)}>
            {t('common.load_more', { defaultValue: 'Load more' })}
          </Button>
        </div>
      )}

      <NeedsAttention items={attention.data?.items || []} loading={attention.loading} filterable showOwner
        health={attentionHealth} onHealth={setAttentionHealth} total={attention.data?.total} />

      <PersonDrilldown employeeId={open?.person.employee_id ?? null} person={open?.person} onClose={() => setOpen(null)}
        staleAfterDays={staleAfterDays} staleBasis={staleBasis} dueSoonDays={dueSoonDays} showUtilization={showUtilization} />
    </div>
  );
};

export const WorkloadDepartments: React.FC<{ onDepartment: (id: string) => void; refreshKey?: number }> = ({ onDepartment, refreshKey = 0 }) => {
  const { t } = useTranslation('work');
  const { data, loading, error } = useWorkloadDepartments(true, refreshKey);
  useEffect(() => {
    if (error) toast.error(getApiErrorMessage(error, t('departments_load_failed', { defaultValue: 'Could not load departments.' })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);
  const rows: WorkloadDepartmentRow[] = data || [];
  return (
    <Card><CardContent className="p-0 overflow-x-auto">
      {loading && !data ? (
        <p className="p-6 text-sm text-muted-foreground flex items-center gap-2" role="status">
          <Loader2 className="w-4 h-4 animate-spin" /> {t('common.loading', { defaultValue: 'Loading…' })}
        </p>
      ) : rows.length === 0 ? (
        <p className="p-6 text-sm text-muted-foreground" data-testid="departments-empty">{t('departments_none', { defaultValue: 'No departments in your workload scope.' })}</p>
      ) : (
        <table className="w-full text-sm" data-testid="departments-table">
          <thead>
            <tr className="text-xs text-muted-foreground">
              <th scope="col" className="text-left font-medium py-2 px-4">{t('col_department', { defaultValue: 'Department' })}</th>
              <th scope="col" className="font-medium py-2 px-2 text-right">{t('col_people', { defaultValue: 'People' })}</th>
              <th scope="col" className="font-medium py-2 px-2 text-right">{t('tile_active', { defaultValue: 'Active' })}</th>
              <th scope="col" className="font-medium py-2 px-2 text-right">{t('tile_due_soon', { defaultValue: 'Due soon' })}</th>
              <th scope="col" className="font-medium py-2 px-2 text-right">{t('tile_overdue', { defaultValue: 'Overdue' })}</th>
              <th scope="col" className="font-medium py-2 px-2 text-right">{t('tile_stale', { defaultValue: 'Stale' })}</th>
              <th scope="col" className="font-medium py-2 px-2 text-right">{t('tile_awaiting_review', { defaultValue: 'Awaiting review' })}</th>
              <th scope="col" className="font-medium py-2 px-2 text-right">{t('col_on_hold', { defaultValue: 'On hold' })}</th>
              <th scope="col" className="py-2 px-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((d) => (
              <tr key={d.department_id} data-testid={`dept-row-${d.department_id}`} className="hover:bg-muted/40 cursor-pointer"
                onClick={() => onDepartment(d.department_id)}>
                <td className="py-2.5 px-4">
                  <button type="button" className="text-foreground hover:underline text-left" onClick={(e) => { e.stopPropagation(); onDepartment(d.department_id); }}>
                    {d.department_name}
                  </button>
                </td>
                <td className="py-2.5 px-2 text-right tabular-nums">{d.people}</td>
                <td className="py-2.5 px-2 text-right tabular-nums">{d.totals?.active ?? 0}</td>
                <td className="py-2.5 px-2 text-right tabular-nums">{d.totals?.due_soon ?? 0}</td>
                <td className="py-2.5 px-2 text-right tabular-nums">{d.totals?.overdue ?? 0}</td>
                <td className="py-2.5 px-2 text-right tabular-nums">{d.totals?.stale ?? 0}</td>
                <td className="py-2.5 px-2 text-right tabular-nums">{d.totals?.awaiting_review ?? 0}</td>
                <td className="py-2.5 px-2 text-right tabular-nums">{d.totals?.paused ?? 0}</td>
                <td className="py-2.5 px-2 text-muted-foreground"><ChevronRight className="w-4 h-4" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </CardContent></Card>
  );
};
